import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import StreakRiskChart from '@/components/StreakRiskChart';

const TIER_META = {
  king:       { emoji: '\u{1F451}', label: 'King', years: '50+' },
  aristocrat: { emoji: '\u{1F3C6}', label: 'Aristocrat', years: '25+' },
  achiever:   { emoji: '\u{1F396}\uFE0F', label: 'Achiever', years: '10+' },
};

/**
 * StreakRiskChartModal
 * --------------------
 * Dialog wrapper around the shared <StreakRiskChart /> component.
 * Triggered from the Dashboard "Streaks At Risk" chip flow when the
 * user wants to inspect a single at-risk ticker without leaving the
 * Dashboard. The inline chart now also lives on every Stock Modal's
 * Dividend Info tab — this modal stays as a low-friction entry point
 * from the chip row.
 */
export default function StreakRiskChartModal({ open, onOpenChange, ticker, riskMeta = null, onOpenFullStock }) {
  const meta = TIER_META[(riskMeta?.dividend_tier || '').toLowerCase()] || null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="streak-risk-chart-modal">
        <DialogHeader className="pr-8">
          <DialogTitle className="flex items-center gap-x-2 gap-y-1 flex-wrap">
            <span className="inline-flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-yellow-500 shrink-0" />
              <span>Streak at Risk · {ticker}</span>
            </span>
            {meta && (
              <span className="text-xs font-normal text-muted-foreground">
                {meta.emoji} {meta.label} ({meta.years} yrs)
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {ticker && (
          <StreakRiskChart ticker={ticker} riskMeta={riskMeta} />
        )}

        <button
          type="button"
          onClick={() => {
            onOpenChange?.(false);
            onOpenFullStock?.(ticker);
          }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-foreground text-background text-xs font-semibold hover:opacity-90 transition-opacity self-start"
          data-testid="streak-risk-open-stock-btn"
        >
          Open full stock detail
          <ExternalLink className="w-3 h-3" />
        </button>
      </DialogContent>
    </Dialog>
  );
}
